import { Button } from '@/components/ui/button';
import { getPlaceholderAvatar } from '@/core/avatar';
import { cn } from '@/lib/utils';
import { Check, Copy, Trash, X } from '@mynaui/icons-react';
import { Eye, Lock, LockOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from './BatchList';
import { Badge } from './ui/badge';

interface ProjectLinkItemProps {
  link: any;
  projectId: string;
  projectName?: string;
  onCopy: (url: string) => void;
  onDelete: (id: string) => void;
  onEditPassword: (link: any) => void;
  isLinkExpired: (expiresAt: string) => boolean;
  deletePending: boolean;
}

export function ProjectLinkItem({
  link,
  projectId,
  projectName,
  onCopy,
  onDelete,
  onEditPassword,
  isLinkExpired,
  deletePending,
}: ProjectLinkItemProps) {
  const { t } = useTranslation('components');
  const url = `${window.location.origin}/shared/${projectId}?token=${link.token}`;
  return (
    <div
      key={link.id}
      className='p-1 pl-2 pb-2 border rounded-xl bg-slate-50 dark:bg-stone-900/40 border-slate-200/70 dark:border-stone-800/70'>
      <div className='flex items-start justify-between'>
        <div className='flex-1'>
          <div className='flex items-center gap-2'>
            <div className='flex flex-1 items-center gap-2 text-sm'>
              <Badge
                className={cn(
                  'dark:bg-stone-800/70 dark:border-stone-800/70 dark:text-white pl-1',
                  link.status === 'ACTIVE'
                    ? 'bg-green-500 text-white dark:bg-emerald-800 dark:border-emerald-800'
                    : 'bg-red-500 text-white dark:bg-rose-800 dark:border-rose-800',
                )}>
                {link.status === 'ACTIVE' ? (
                  <Check className='size-4 stroke-2 mr-0.5' />
                ) : (
                  <X className='size-4 stroke-2 mr-0.5' />
                )}
                {link.status}
              </Badge>
              {link.hasPassword && (
                <Badge variant='outline' className='dark:bg-stone-800/70 dark:border-stone-800/70 dark:text-white'>
                  {t('shareDialog.password')}
                </Badge>
              )}
              <span className='text-slate-900 dark:text-white max-w-32 overflow-ellipsis text-ellipsis whitespace-nowrap'>
                {link.name || t('shareDialog.untitled')}
              </span>
              {projectName && (
                <>
                  &middot;
                  <span className='text-slate-900 dark:text-white font-medium'>{projectName}</span>
                </>
              )}
            </div>
            <Button
              size='sm'
              variant='ghost'
              className='size-8'
              onClick={() => onCopy(url)}
              disabled={isLinkExpired(link.expiresAt)}>
              <Copy className='size-4 stroke-2' />
            </Button>
            <Button
              size='sm'
              variant='ghost'
              className='size-8'
              title={link.hasPassword ? t('shareDialog.changeOrRemovePassword') : t('shareDialog.setPasswordTitle')}
              onClick={() => onEditPassword(link)}>
              {link.hasPassword ? <Lock className='size-4' /> : <LockOpen className='size-4' />}
            </Button>
            <Button
              size='sm'
              variant='ghost'
              className='size-8 text-rose-500 hover:text-rose-600'
              onClick={() => onDelete(link.id)}
              disabled={deletePending}>
              <Trash className='size-4 stroke-2' />
            </Button>
          </div>
          <div className='text-xs pr-2 pl-2 mt-1 font-medium items-center text-slate-700 dark:text-stone-400 flex gap-2'>
            <span>{formatDateTime(link.createdAt)}</span>&middot;
            <span>{formatDateTime(link.expiresAt)}</span>
            {link.viewCount != null && (
              <>
                &middot;
                <span className='flex items-center gap-1'>
                  <Eye className='size-3 stroke-2' />
                  {link.viewCount}
                </span>
              </>
            )}
            <div className='flex-1'></div>
            <span className='flex items-center gap-1'>
              {t('shareDialog.createdBy')}{' '}
              <img
                src={
                  link.createdBy?.avatar?.url || getPlaceholderAvatar(link.createdBy?.email || link.createdBy?.id || '')
                }
                alt={link.createdBy?.name || link.createdBy?.email || t('shareDialog.unknown')}
                className='size-4 rounded-full object-cover border border-slate-200/70 dark:border-stone-800/70'
              />{' '}
              {link.createdBy?.name || link.createdBy?.email || t('shareDialog.unknown')}
            </span>
          </div>
        </div>
        <div className='flex items-center'></div>
      </div>
    </div>
  );
}
